import type { ChatItem } from './sessionChat'
import type { ToolActivity } from './toolActivity'

// 会話に描く行。同じツールが続いた呼び出しは、1 つの束にまとめて描く。
// 会話の状態と会話記録の読み方は変えず、描く直前にここで束ねる。会話記録から作り直したときも同じまとまりになる。

// 会話に出すツールの呼び出し。コマンドの実行とファイルの編集（activity が null）は出さない（toolActivity.ts）
type ShownToolItem = Extract<ChatItem, { kind: 'tool' }> & { activity: ToolActivity }

// 束の終わり方。実行中の呼び出しが残っていれば null
type ToolGroupStatus = 'completed' | 'failed' | 'interrupted'

export type ChatRow =
  | { kind: 'item'; item: ChatItem }
  // id は束の最初の呼び出しの id。実行中に束が伸びても、開いた状態を保つ。
  // activity は束の見出しの一行で、呼び出しごとに違いうる対象の名前は入れない
  | {
      kind: 'toolGroup'
      id: string
      activity: ToolActivity
      status: ToolGroupStatus | null
      tools: readonly ShownToolItem[]
    }

const isShownToolItem = (item: ChatItem): item is ShownToolItem => item.kind === 'tool' && item.activity !== null

const isShownItem = (item: ChatItem): boolean => item.kind !== 'tool' || item.activity !== null

const isSameTool = (a: ShownToolItem, b: ShownToolItem): boolean =>
  a.turnId === b.turnId && a.activity.kind === b.activity.kind && a.activity.tool === b.activity.tool

// 前の項目と同じ束に入るか。束に入るのは、同じツールが続いた呼び出しだけである
const continuesToolRun = (previous: ChatItem | undefined, item: ChatItem): boolean =>
  previous !== undefined && isShownToolItem(previous) && isShownToolItem(item) && isSameTool(previous, item)

// 失敗を束の中に隠さないよう、1 件でも失敗があれば失敗にする
const toolGroupStatusOf = (tools: readonly ShownToolItem[]): ToolGroupStatus | null => {
  if (tools.some((tool) => tool.status === 'failed')) {
    return 'failed'
  }
  if (tools.some((tool) => tool.status === 'interrupted')) {
    return 'interrupted'
  }
  return tools.every((tool) => tool.status === 'completed') ? 'completed' : null
}

const groupActivityOf = (activity: ToolActivity): ToolActivity =>
  activity.kind === 'salescoreMcp' ? { ...activity, subject: null } : activity

// 続いた項目の区間を行にする。2 件以上の区間は、同じツールが続いた呼び出しだけからなる
const chatRowsOfRun = (run: readonly ChatItem[]): readonly ChatRow[] => {
  const tools = run.filter(isShownToolItem)
  const [first] = tools
  if (first === undefined || tools.length < 2) {
    return run.map((item) => ({ kind: 'item', item }))
  }
  return [
    {
      kind: 'toolGroup',
      id: first.id,
      activity: groupActivityOf(first.activity),
      status: toolGroupStatusOf(tools),
      tools,
    },
  ]
}

/**
 * 会話の項目を、描く行にする。同じターンで同じツール（salescore-mcp は同じツール、ほかは同じツール名）が
 * 2 件以上続いたら 1 つの束にする。指示、回答、承認のカード、ターンの終わりなどのツール以外の項目は続きを切り、
 * 会話に出さない呼び出しは続きを切らずに行から外す。
 * 回答の続きが届くたびに会話全体から作り直すので、項目を足すたびに配列を写し直す形にはせず、
 * 束の始まりの位置を集めて区間ごとに切り出し、項目数に比例する時間で済ませる
 */
export const chatRowsOf = (items: readonly ChatItem[]): readonly ChatRow[] => {
  const shown = items.filter(isShownItem)
  const starts = shown.flatMap((item, index) => (index > 0 && continuesToolRun(shown[index - 1], item) ? [] : [index]))
  return starts.flatMap((start, order) => chatRowsOfRun(shown.slice(start, starts[order + 1] ?? shown.length)))
}
